/** Rolling billing-period helpers (default 31-day window from start date). */

import { normalizeDateOnly } from './dateOnlyUtils.js';

export const DEFAULT_BILLING_PERIOD_DAYS = 31;

const parseYmdDate = (ymd) => {
  const match = String(ymd || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(Number.parseInt(match[1], 10), Number.parseInt(match[2], 10) - 1, Number.parseInt(match[3], 10));
};

const formatYmdDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Inclusive day count between two YYYY-MM-DD dates. */
export const countInclusiveDays = (startYmd, endYmd) => {
  const start = parseYmdDate(startYmd);
  const end = parseYmdDate(endYmd);
  if (!start || !end) return 0;
  const diffMs = end.getTime() - start.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;
  return diffDays > 0 ? diffDays : 0;
};

export const getCalendarMonthPeriod = (referenceDate = new Date()) => {
  let year;
  let monthIndex;

  if (typeof referenceDate === 'string') {
    const match = String(referenceDate).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      year = Number.parseInt(match[1], 10);
      monthIndex = Number.parseInt(match[2], 10) - 1;
    }
  }

  if (year == null || monthIndex == null) {
    const ref = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
    year = ref.getFullYear();
    monthIndex = ref.getMonth();
  }

  const month = String(monthIndex + 1).padStart(2, '0');
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  const dateFrom = `${year}-${month}-01`;
  const dateTo = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;

  return { dateFrom, dateTo, daysInMonth: lastDay };
};

/** Today's date as YYYY-MM-DD (local). */
export const getTodayYmd = () => formatYmdDate(new Date());

/**
 * Billing period from an actual start date: start = selected day, end = start + 31 days inclusive.
 * Example: start 2026-04-04 -> end 2026-05-04 (31 days).
 */
export const getBillingPeriodFromStartDate = (
  referenceDate = new Date(),
  numDays = DEFAULT_BILLING_PERIOD_DAYS
) => {
  let year;
  let monthIndex;
  let day;

  if (typeof referenceDate === 'string') {
    const match = String(referenceDate).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      year = Number.parseInt(match[1], 10);
      monthIndex = Number.parseInt(match[2], 10) - 1;
      day = Number.parseInt(match[3], 10);
    }
  }

  if (year == null || monthIndex == null || day == null) {
    const ref = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
    year = ref.getFullYear();
    monthIndex = ref.getMonth();
    day = ref.getDate();
  }

  const month = String(monthIndex + 1).padStart(2, '0');
  const dayStr = String(day).padStart(2, '0');
  const dateFrom = `${year}-${month}-${dayStr}`;
  const periodDays = Number.parseInt(String(numDays), 10) || DEFAULT_BILLING_PERIOD_DAYS;
  const dateTo = computeEndDateFromDays(dateFrom, periodDays);

  return { dateFrom, dateTo, daysInMonth: periodDays };
};

/** End date when counting `numDays` inclusively from startYmd. */
export const computeEndDateFromDays = (startYmd, numDays) => {
  const start = parseYmdDate(startYmd);
  const days = Number.parseInt(String(numDays), 10);
  if (!start || !Number.isFinite(days) || days <= 0) return '';
  const end = new Date(start);
  end.setDate(end.getDate() + days - 1);
  return formatYmdDate(end);
};

/** Add calendar days to a YYYY-MM-DD string (local, no UTC shift). */
export const addDaysYmd = (startYmd, daysToAdd) => {
  const start = parseYmdDate(startYmd);
  const days = Number.parseInt(String(daysToAdd), 10);
  if (!start || !Number.isFinite(days)) return '';
  const next = new Date(start);
  next.setDate(next.getDate() + days);
  return formatYmdDate(next);
};

const cycleKey = (start, end) => `${start}|${end}`;

/**
 * Rolling + saved billing windows for the practice billing date-range dropdown.
 * Newest cycles first. Dedupes overlapping saved/generated ranges.
 */
export const buildBillingCycleList = ({
  originStart,
  periodDays = DEFAULT_BILLING_PERIOD_DAYS,
  untilYmd = getTodayYmd(),
  savedCycles = [],
  currentStart = null,
  currentEnd = null
} = {}) => {
  const cycles = [];
  const seen = new Set();
  const days = Number.parseInt(String(periodDays), 10) || DEFAULT_BILLING_PERIOD_DAYS;
  const today = getTodayYmd();

  const addCycle = (start, end, extra = {}) => {
    const from = normalizeDateOnly(start);
    const to = normalizeDateOnly(end);
    if (!from || !to || to < from) return;
    // Strictly isolate: never display or generate cycles that start after today
    if (from > today) return;
    const key = cycleKey(from, to);
    if (seen.has(key)) return;
    seen.add(key);
    cycles.push({
      key,
      start: from,
      end: to,
      ...extra
    });
  };

  // 1. Prepare and filter saved historical cycles
  let sortedSaved = (Array.isArray(savedCycles) ? savedCycles : [])
    .map((row) => ({
      start: normalizeDateOnly(row.rpm_start_date || row.start_date || row.start),
      end: normalizeDateOnly(row.rpm_end_date || row.end_date || row.end),
      note: row.note || '',
      days: row.number_of_days || null
    }))
    .filter((row) => row.start && row.end && row.end >= row.start && row.start <= today)
    .sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));

  const currentFrom = normalizeDateOnly(currentStart);
  const currentTo = normalizeDateOnly(currentEnd);

  // If a saved cycle is within or overlaps with the active practice cycle (e.g. 10/01 ~ 10/15 is within 10/01 ~ 10/30),
  // merge it out so the active cycle remains the single authoritative cycle for that period.
  if (currentFrom && currentTo && currentFrom <= today) {
    sortedSaved = sortedSaved.filter((row) => {
      if (row.start === currentFrom) return false;
      if (row.start >= currentFrom && row.end <= currentTo) return false;
      if (row.start >= currentFrom) return false;
      return true;
    });
  }

  // Also merge any saved cycle that is completely contained within another saved cycle
  sortedSaved = sortedSaved.filter((row, idx) => {
    const isContained = sortedSaved.some((other, oIdx) => {
      if (idx === oIdx) return false;
      if (other.start <= row.start && other.end >= row.end) {
        if (other.start < row.start || other.end > row.end) return true;
        return idx < oIdx;
      }
      return false;
    });
    return !isContained;
  });

  sortedSaved.forEach((row) => {
    addCycle(row.start, row.end, { source: 'saved', note: row.note, days: row.days });
  });

  // 2. Add current active cycle
  if (currentFrom && currentTo && currentFrom <= today) {
    addCycle(currentFrom, currentTo, { source: 'current' });
  }

  // 3. Gap filling: Only generate cycles where historical saved cycles do not exist
  if (sortedSaved.length === 0) {
    let from = normalizeDateOnly(originStart) || currentFrom;
    let index = 1;
    while (from && from <= today && index <= 120) {
      if (currentFrom && from >= currentFrom) break;
      const to = computeEndDateFromDays(from, days);
      if (!to) break;
      if (currentFrom && to >= currentFrom) break;
      addCycle(from, to, { source: 'generated', days });
      from = addDaysYmd(to, 1);
      index += 1;
    }
  } else {
    // Gap before first saved cycle if practice started earlier
    const firstSaved = sortedSaved[0];
    const origin = normalizeDateOnly(originStart);
    if (origin && origin < firstSaved.start) {
      let from = origin;
      let index = 1;
      while (from && from < firstSaved.start && index <= 120) {
        const to = computeEndDateFromDays(from, days);
        if (!to || to >= firstSaved.start) break;
        addCycle(from, to, { source: 'generated', days });
        from = addDaysYmd(to, 1);
        index += 1;
      }
    }

    // Gap after latest saved cycle before current cycle starts
    const latestSaved = sortedSaved[sortedSaved.length - 1];
    let from = addDaysYmd(latestSaved.end, 1);
    let index = 1;
    while (from && from <= today && index <= 120) {
      if (currentFrom && from >= currentFrom) break;
      const to = computeEndDateFromDays(from, days);
      if (!to) break;
      if (currentFrom && to >= currentFrom) break;
      addCycle(from, to, { source: 'generated', days });
      from = addDaysYmd(to, 1);
      index += 1;
    }
  }

  cycles.sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
  cycles.forEach((cycle, index) => {
    cycle.cycleNumber = index + 1;
  });
  cycles.reverse();
  return cycles;
};

export const findBillingCycleKey = (cycles, startYmd, endYmd) => {
  const from = normalizeDateOnly(startYmd);
  const to = normalizeDateOnly(endYmd);
  if (!from || !to) return '';
  const match = (Array.isArray(cycles) ? cycles : []).find(
    (cycle) => cycle.start === from && cycle.end === to
  );
  return match?.key || '';
};

export const getPracticeBillingPeriod = (practice) => {
  const start =
    practice?.billing_from_date ||
    practice?.rpm_start_date ||
    null;
  const end =
    practice?.billing_to_date ||
    practice?.rpm_end_date ||
    null;
  return {
    start: normalizeDateOnly(start),
    end: normalizeDateOnly(end)
  };
};
